import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let updatedValue = value;

    // Mobile number: digits only, max 10
    if (name === 'phone') {
      updatedValue = value.replace(/\D/g, '').slice(0, 10);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: updatedValue
    }));

    setFieldErrors((prev) => ({
      ...prev,
      [name]: ''
    }));
  };

  const validate = () => {
    const errors = {};

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[6-9]\d{9}$/;

    if (!emailRegex.test(formData.email)) {
      errors.email = 'Enter valid email address';
    }

    if (!phoneRegex.test(formData.phone)) {
      errors.phone = 'Enter valid 10-digit mobile number';
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(
        'https://amaanativf.com/api/contact_submit.php',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: '',
        });
      } else {
        setError(data.message || 'Submission failed');
      }
    } catch (error) {
      setError('Server error. Try again later.');
    }

    setLoading(false);
  };

  return (
    <div className="contact-page">

      {/* ✅ SEO START */}
      <Helmet>
        <title>Contact IVF Centre in Ambala | Amaanat IVF Clinic</title>
        <meta
          name="description"
          content="Contact Amaanat IVF Clinic in Ambala for IVF, ICSI, IUI treatments. Call +91 9991641100 or visit our fertility centre."
        />
        <link rel="canonical" href="https://amaanativf.com/contact" />
      </Helmet>
      {/* ✅ SEO END */}

      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <h1 className="hero-title">Contact Amaanat IVF in Ambala</h1>
          <p className="hero-subtitle">
            Start your fertility journey today with Amaanat IVF
          </p>

          <div className="contact-emergency">
            <div className="emergency-phone">
              <span className="phone-icon">📞</span>
              <a href="tel:+919991641100" className="phone-number">
                +91 9991641100
              </a>
            </div>

            <Link to="/bookappointment" className="btn-appointmenttt">
              Book Appointment
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="contact-info">
        <div className="container">
          <div className="info-grid">
            <div className="info-card phone-card">
              <div className="info-icon"><i className="fa-solid fa-phone"></i></div>
              <h3>Call IVF Clinic</h3>
              <a href="tel:+9991641100" className="contact-link">
                +91 9991641100
              </a>
            </div>

            <div className="info-card email-card">
              <div className="info-icon"><i className="fa-regular fa-envelope"></i></div>
              <h3>Email Us</h3>
              <a href="mailto:info@amaanativf.com" className="contact-link">
                info@amaanativf.com
              </a>
            </div>

            <div className="info-card location-card">
              <div className="info-icon"><i className="fa-solid fa-map-pin"></i></div>
              <h3>Visit IVF Clinic</h3>
              <p>Mon-Sat: 10AM - 7PM</p>
              <div className="address">
                <strong>
                  15, Medha Tower Kalka Chowk <br />
                  Ambala City, Haryana, India - 134003
                </strong>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="contact-form-section">
        <div className="container">
          <div className="form-container">
            <div className="form-content">
              <h2 className="section-title">Send Message to IVF Experts</h2>

              {isSubmitted && (
                <div className="success-message">
                  ✅ Message sent successfully. Our IVF team will contact you soon.
                </div>
              )}

              {error && <div className="form-error">{error}</div>}

              <form className="contact-form" onSubmit={handleSubmit} noValidate>

                <div className="form-row">
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="Full Name *"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number *"
                      value={formData.phone}
                      onChange={handleInputChange}
                      inputMode="numeric"
                      required
                    />
                    {fieldErrors.phone && (
                      <p className="field-error">{fieldErrors.phone}</p>
                    )}
                  </div>
                </div>

                <div>
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address *"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                  {fieldErrors.email && (
                    <p className="field-error">{fieldErrors.email}</p>
                  )}
                </div>

                <select
                  name="service"
                  value={formData.service}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Service *</option>
                  <option value="ivf">IVF Treatment</option>
                  <option value="iui">IUI Procedure</option>
                  <option value="icsi">ICSI Treatment</option>
                  <option value="consultation">Free Consultation</option>
                  <option value="other">Other</option>
                </select>

                <textarea
                  name="message"
                  placeholder="Your Message *"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows="5"
                  required
                ></textarea>

                <button
                  type="submit"
                  className={`form-btn ${loading ? 'loading' : ''}`}
                  disabled={loading || isSubmitted}
                >
                  {loading ? (
                    <>
                      <span className="spinner"></span> Submitting...
                    </>
                  ) : isSubmitted ? (
                    'Sent!'
                  ) : (
                    'Send Message'
                  )}
                </button>

              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="location-section">
        <div className="container">
          <h2 className="section-title">IVF Clinic Location in Ambala</h2>
          <div className="map-container">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d666.5532390487556!2d76.79094236364969!3d30.37918216993418!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390fc97b6f137585%3A0xf6e4386d9556155d!2sAmaanat%20ivf%20%26%20fertility%20center%20franchise%20of%20Aashakiran!5e0!3m2!1sen!2sin!4v1774435691478!5m2!1sen!2sin"
              width="100%"
              height="400"
              style={{ border: 0 }}
              loading="lazy"
              title="Amaanat IVF Clinic Location Map"
            />
          </div>
        </div>
      </section>

    </div>
  );
};

export default Contact;